import React, { useCallback, useEffect, useState } from 'react';
import API from '../../api/axiosInstance';
import '../css/ParticipantList.css';
import '../css/PeopleDataCleanup.css';

const STEPS = ['Find', 'Compare', 'Delete'];
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const STATUS_LABELS = {
  same_name_different_emails: 'Same name, different emails',
  shared_email_different_names: 'Shared email, different names',
  duplicate_email_same_person: 'Same email, same person',
  duplicate_name_no_distinct_email: 'Same name, no distinct email',
};

const formatStatusReason = (reason) => {
  if (!reason) return '';
  if (STATUS_LABELS[reason]) return STATUS_LABELS[reason];
  return reason.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const groupStatus = (group) => {
  if (group.mergeable) {
    return {
      label: 'Mergeable',
      badgeClass: 'pdc-badge pdc-badge--mergeable',
      reason: formatStatusReason(group.reason),
    };
  }
  return {
    label: 'Needs review',
    badgeClass: 'pdc-badge pdc-badge--review',
    reason: formatStatusReason(group.skipReason) || 'Review before merge',
  };
};

const personDisplayName = (p) => `${p.name || ''} ${p.lname || ''}`.trim() || '(no name)';

const uniqueNames = (persons = []) => {
  const seen = new Set();
  const names = [];
  persons.forEach((p) => {
    const label = personDisplayName(p);
    const key = label.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      names.push(label);
    }
  });
  return names;
};

const nameTokens = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);

const isSimilarName = (selected, candidate) => {
  const a = String(selected || '').toLowerCase().trim();
  const b = String(candidate || '').toLowerCase().trim();
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.includes(b) || b.includes(a)) return true;
  const ta = nameTokens(a);
  const tb = nameTokens(b);
  if (!ta.length || !tb.length) return false;
  const lastA = ta[ta.length - 1];
  const lastB = tb[tb.length - 1];
  if (lastA.length > 2 && lastA === lastB) return true;
  if (ta[0] === tb[0] && lastA === lastB) return true;
  const shared = ta.filter((t) => t.length > 2 && tb.includes(t));
  return shared.length >= 2;
};

const groupSimilarity = (group, selected) => {
  const sel = String(selected || '').toLowerCase().trim();
  if (!sel) return null;

  if (group.match === 'name') {
    if (group.key === sel) return 'exact';
    if (isSimilarName(sel, group.key)) return 'similar';
    return null;
  }

  const names = uniqueNames(group.persons).map((n) => n.toLowerCase());
  if (group.key === sel || names.includes(sel)) return 'exact';
  if (names.some((n) => isSimilarName(sel, n))) return 'similar';
  return null;
};

const emailGroupSimilarity = (group, selectedEmail) => {
  if (group.match !== 'email') return null;
  const sel = String(selectedEmail || '').toLowerCase().trim();
  const key = String(group.key || '').toLowerCase().trim();
  if (!sel || !key) return null;
  if (key === sel) return 'exact';
  if (key.includes(sel) || sel.includes(key)) return 'similar';
  return null;
};

const PeopleDataCleanup = () => {
  const [step, setStep] = useState(0);
  const [match, setMatch] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedName, setSelectedName] = useState('');
  const [selectedEmail, setSelectedEmail] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'mergeable' | 'review'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [detail, setDetail] = useState(null);
  const [keepId, setKeepId] = useState('');
  const [deleteIds, setDeleteIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await API.get('/ems/people-dedup/groups', { params: { match } });
      setSummary({
        database: res.data.database,
        peopleTotal: res.data.peopleTotal,
        mergeableCount: res.data.mergeableCount,
        skippedCount: res.data.skippedCount,
      });
      setGroups(Array.isArray(res.data.groups) ? res.data.groups : []);
      setSelectedName('');
      setSelectedEmail('');
      setStatusFilter('');
      setCurrentPage(1);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load groups');
      setGroups([]);
      setSelectedName('');
      setSelectedEmail('');
      setStatusFilter('');
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  }, [match]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const statusScopedGroups = (() => {
    if (statusFilter === 'mergeable') return groups.filter((g) => g.mergeable);
    if (statusFilter === 'review') return groups.filter((g) => !g.mergeable);
    return groups;
  })();

  const nameOptions = (() => {
    const seen = new Set();
    const opts = [];
    statusScopedGroups.forEach((g) => {
      if (g.match !== 'name') return;
      if (!seen.has(g.key)) {
        seen.add(g.key);
        opts.push(g.key);
      }
    });
    return opts.sort((a, b) => a.localeCompare(b));
  })();

  const emailOptions = (() => {
    const seen = new Set();
    const opts = [];
    statusScopedGroups.forEach((g) => {
      if (g.match !== 'email') return;
      const email = String(g.key || '').trim();
      if (!email || seen.has(email)) return;
      seen.add(email);
      opts.push(email);
    });
    return opts.sort((a, b) => a.localeCompare(b));
  })();

  const showNameFilter = match === 'all' || match === 'name';
  const showEmailFilter = match === 'all' || match === 'email';
  const hasSelection = !!(selectedName || selectedEmail);
  const hasList = hasSelection || !!statusFilter;

  const filteredGroups = (() => {
    if (!hasList) return [];
    const map = new Map();
    const add = (g, similarity) => {
      const id = `${g.match}:${g.key}`;
      const prev = map.get(id);
      if (!prev || (similarity === 'exact' && prev.similarity !== 'exact')) {
        map.set(id, { ...g, similarity });
      }
    };
    if (!hasSelection && statusFilter) {
      statusScopedGroups.forEach((g) => add(g, 'exact'));
    } else {
      if (selectedName) {
        statusScopedGroups.forEach((g) => {
          const similarity = groupSimilarity(g, selectedName);
          if (similarity) add(g, similarity);
        });
      }
      if (selectedEmail) {
        statusScopedGroups.forEach((g) => {
          const similarity = emailGroupSimilarity(g, selectedEmail);
          if (similarity) add(g, similarity);
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.similarity !== b.similarity) return a.similarity === 'exact' ? -1 : 1;
      return b.count - a.count || a.key.localeCompare(b.key);
    });
  })();

  const toggleStatusFilter = (next) => {
    setStep(0);
    setStatusFilter((prev) => (prev === next ? '' : next));
    setSelectedName('');
    setSelectedEmail('');
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * itemsPerPage;
  const pageGroups = filteredGroups.slice(pageStart, pageStart + itemsPerPage);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  const visiblePages = (() => {
    if (totalPages <= 5) return pageNumbers;
    if (safePage <= 3) return pageNumbers.slice(0, 5);
    if (safePage >= totalPages - 2) return pageNumbers.slice(-5);
    return pageNumbers.slice(safePage - 3, safePage + 2);
  })();

  useEffect(() => {
    if (currentPage !== safePage) setCurrentPage(safePage);
  }, [currentPage, safePage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedName, selectedEmail, statusFilter]);

  const openCompare = async (group) => {
    setPreview(null);
    setApplyResult(null);
    setLoading(true);
    setError('');
    try {
      const res = await API.get('/ems/people-dedup/group', {
        params: { match: group.match, key: group.key },
      });
      setDetail(res.data);
      const suggested = res.data.suggestedKeepId || '';
      setKeepId(suggested);
      setDeleteIds((res.data.persons || []).map((p) => p._id).filter((id) => id !== suggested));
      setStep(1);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load group');
    } finally {
      setLoading(false);
    }
  };

  const toggleDelete = (id) => {
    if (id === keepId) return;
    setDeleteIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setPreview(null);
  };

  const onPickKeep = (id) => {
    setKeepId(id);
    setDeleteIds((detail?.persons || []).map((p) => p._id).filter((x) => x !== id));
    setPreview(null);
  };

  const runPreview = async () => {
    setLoading(true);
    setError('');
    setApplyResult(null);
    try {
      const res = await API.post('/ems/people-dedup/preview', { keepId, deleteIds });
      setPreview(res.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm('Permanently reassign FKs and delete duplicate people? This cannot be undone.')) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await API.post('/ems/people-dedup/apply', { keepId, deleteIds, confirm: true });
      setApplyResult(res.data);
      setPreview(null);
      await loadGroups();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Apply failed');
    } finally {
      setLoading(false);
    }
  };

  const backToFind = () => {
    setStep(0);
    setDetail(null);
    setPreview(null);
    setApplyResult(null);
  };

  const detailStatus = detail ? groupStatus(detail) : null;

  return (
    <div className="pdc-page">
      <h4 className="pdc-title">People Duplicate Cleanup</h4>
      <p className="pdc-subtitle">
        Find duplicate people by name/email, compare usage across collections, then reassign and delete.
      </p>
      {summary && (
        <div className="pdc-summary">
          <span className="pdc-summary-chip">
            <span>DB</span> {summary.database}
          </span>
          <span className="pdc-summary-chip">
            <span>People</span> {summary.peopleTotal}
          </span>
          <button
            type="button"
            className={`pdc-summary-chip pdc-summary-chip--ok pdc-summary-chip--clickable${
              statusFilter === 'mergeable' ? ' is-active' : ''
            }`}
            onClick={() => toggleStatusFilter('mergeable')}
            aria-pressed={statusFilter === 'mergeable'}
            title="Show mergeable duplicate groups"
          >
            <span>Mergeable</span> {summary.mergeableCount}
          </button>
          <button
            type="button"
            className={`pdc-summary-chip pdc-summary-chip--warn pdc-summary-chip--clickable${
              statusFilter === 'review' ? ' is-active' : ''
            }`}
            onClick={() => toggleStatusFilter('review')}
            aria-pressed={statusFilter === 'review'}
            title="Show groups that need review"
          >
            <span>Need review</span> {summary.skippedCount}
          </button>
        </div>
      )}

      <div className="pdc-steps">
        {STEPS.map((label, i) => (
          <span key={label} className={`pdc-step ${step === i ? 'is-active' : ''}`}>
            {i + 1}. {label}
          </span>
        ))}
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {loading && <div className="text-muted mb-2">Loading…</div>}

      {step === 0 && (
        <>
          <div className="pdc-toolbar">
            <label htmlFor="pdc-match">Type</label>
            <select
              id="pdc-match"
              className="form-select form-select-sm pdc-select-type"
              value={match}
              onChange={(e) => setMatch(e.target.value)}
            >
              <option value="all">All</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
            </select>

            {showNameFilter && (
              <>
                <label htmlFor="pdc-name">Name</label>
                <select
                  id="pdc-name"
                  className="form-select form-select-sm pdc-select-name"
                  value={selectedName}
                  onChange={(e) => setSelectedName(e.target.value)}
                  disabled={!nameOptions.length}
                >
                  <option value="">
                    {nameOptions.length ? 'Select a name…' : 'No duplicate names'}
                  </option>
                  {nameOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </>
            )}

            {showEmailFilter && (
              <>
                <label htmlFor="pdc-email">Email</label>
                <select
                  id="pdc-email"
                  className="form-select form-select-sm pdc-select-email"
                  value={selectedEmail}
                  onChange={(e) => setSelectedEmail(e.target.value)}
                  disabled={!emailOptions.length}
                >
                  <option value="">
                    {emailOptions.length ? 'Select a duplicate email…' : 'No duplicate emails'}
                  </option>
                  {emailOptions.map((email) => (
                    <option key={email} value={email}>
                      {email}
                    </option>
                  ))}
                </select>
              </>
            )}

            {(hasSelection || statusFilter) && (
              <button
                type="button"
                className="pdc-toolbar-btn"
                onClick={() => {
                  setSelectedName('');
                  setSelectedEmail('');
                  setStatusFilter('');
                }}
              >
                Clear
              </button>
            )}
            <button
              type="button"
              className="pdc-toolbar-btn"
              onClick={loadGroups}
            >
              Refresh
            </button>
          </div>

          <div className="pdc-table-container">
            <table className="pdc-table">
              <colgroup>
                <col className="pdc-col-names" />
                <col className="pdc-col-count" />
                <col className="pdc-col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">{match === 'email' ? 'Email' : 'Name'}</th>
                  <th scope="col">Count</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {pageGroups.map((g) => (
                  <tr key={`${g.match}:${g.key}`}>
                    <td className={g.match === 'email' ? 'pdc-email' : 'pdc-names'}>
                      {g.key}
                    </td>
                    <td className="pdc-cell-count">{g.count}</td>
                    <td className="pdc-cell-action">
                      <button
                        type="button"
                        className="pdc-compare-btn"
                        onClick={() => openCompare(g)}
                      >
                        Compare
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && groups.length === 0 && (
                  <tr>
                    <td colSpan={3} className="pdc-empty">No duplicate groups found.</td>
                  </tr>
                )}
                {!loading && groups.length > 0 && !hasList && (
                  <tr>
                    <td colSpan={3} className="pdc-empty">
                      Click Mergeable or Need review above, or select a name/email filter.
                    </td>
                  </tr>
                )}
                {!loading && hasList && filteredGroups.length === 0 && (
                  <tr>
                    <td colSpan={3} className="pdc-empty">
                      No matching groups for this selection.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {hasList && filteredGroups.length > 0 && (
              <div className="participant-pagination">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="small text-muted">
                    Showing {pageStart + 1}–{Math.min(pageStart + itemsPerPage, filteredGroups.length)} of {filteredGroups.length}
                  </span>
                  <span style={{ fontSize: '13px', color: '#666' }}>Show</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto', fontSize: '13px' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>

                <div className="pagination-box">
                  <button
                    type="button"
                    className="pagination-btn"
                    disabled={safePage === 1}
                    onClick={() => setCurrentPage(safePage - 1)}
                  >
                    Previous
                  </button>
                  {visiblePages.map((num) => (
                    <button
                      key={num}
                      type="button"
                      className={`pagination-btn ${num === safePage ? 'active' : ''}`}
                      onClick={() => setCurrentPage(num)}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="pagination-btn"
                    disabled={safePage === totalPages}
                    onClick={() => setCurrentPage(safePage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {step === 1 && detail && detailStatus && (
        <>
          <button type="button" className="pdc-back-link" onClick={backToFind}>
            ← Back to list
          </button>

          <div className="pdc-compare-head">
            <span className="pdc-compare-title text-capitalize">
              {detail.match}: {detail.key}
            </span>
            <span className={detailStatus.badgeClass}>{detailStatus.label}</span>
            {detailStatus.reason && (
              <span className="pdc-status-reason">{detailStatus.reason}</span>
            )}
          </div>

          <div className="pdc-table-container">
            <table className="pdc-table pdc-compare-table">
              <thead>
                <tr>
                  <th scope="col" aria-label="Keep"></th>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Participants</th>
                  <th scope="col">Roles</th>
                  <th scope="col">Relations</th>
                  <th scope="col">Documents</th>
                  <th scope="col">Changelogs</th>
                  <th scope="col">Delete</th>
                </tr>
              </thead>
              <tbody>
                {(detail.persons || []).map((p) => {
                  const isKeep = keepId === p._id;
                  return (
                    <tr key={p._id} className={isKeep ? 'pdc-row-keep' : ''}>
                      <td>
                        <input
                          type="radio"
                          name="keepPerson"
                          checked={isKeep}
                          onChange={() => onPickKeep(p._id)}
                          aria-label={`Keep ${personDisplayName(p)}`}
                        />
                      </td>
                      <td className="pdc-names">{personDisplayName(p)}</td>
                      <td>{p.email || '—'}</td>
                      <td>{p.usage?.participants ?? 0}</td>
                      <td>{p.usage?.personroles ?? 0}</td>
                      <td>{p.usage?.personassistantrelations ?? 0}</td>
                      <td>{p.usage?.documents ?? 0}</td>
                      <td>{p.usage?.changelogs ?? 0}</td>
                      <td>
                        {!isKeep ? (
                          <input
                            type="checkbox"
                            checked={deleteIds.includes(p._id)}
                            onChange={() => toggleDelete(p._id)}
                            aria-label={`Delete ${personDisplayName(p)} after merge`}
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="pdc-compare-actions">
            <button
              type="button"
              className="pdc-compare-btn"
              disabled={!keepId || deleteIds.length === 0 || loading}
              onClick={runPreview}
            >
              Preview delete
            </button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <button
            type="button"
            className="btn btn-sm btn-link mb-2 px-0"
            onClick={() => { setStep(1); setApplyResult(null); }}
          >
            ← Back to compare
          </button>

          {applyResult ? (
            <div className="alert alert-success">
              <div className="fw-semibold mb-1">Merge applied</div>
              <div className="small">People deleted: {applyResult.stats?.peopleDeleted}</div>
              <div className="small">Participants moved: {applyResult.stats?.participants}</div>
              <div className="small">Person roles moved: {applyResult.stats?.personrolesMoved}</div>
              <div className="small">Documents moved/removed: {applyResult.stats?.documentsMoved}/{applyResult.stats?.documentsRemoved}</div>
              <div className="small">Changelogs: {applyResult.stats?.changelogs}</div>
              <button type="button" className="btn btn-sm btn-outline-primary mt-2" onClick={backToFind}>
                Back to list
              </button>
            </div>
          ) : preview ? (
            <>
              <h6>Preview</h6>
              <p className="small">
                Keep {preview.keep?.name} {preview.keep?.lname}
                · delete {preview.delete?.length} duplicate(s)
              </p>
              <pre className="bg-light border rounded p-2 small" style={{ maxHeight: 240, overflow: 'auto' }}>
                {JSON.stringify(preview.reassignments, null, 2)}
              </pre>
              <button
                type="button"
                className="btn btn-danger"
                disabled={loading}
                onClick={runApply}
              >
                Confirm reassign &amp; delete
              </button>
            </>
          ) : (
            <p className="text-muted">No preview yet.</p>
          )}
        </>
      )}
    </div>
  );
};

export default PeopleDataCleanup;
